
import { useEffect, useState } from 'react';
import AboutUs from '../UI/Sections/AboutUs';
import AccountOpenPage from '../UI/Sections/AccountOpenPage';
import BlogPostDetail from '../UI/Sections/BlogPostDetail';
import Downloads from '../UI/Sections/Downloads';
import Footer from '../UI/Sections/Footer';
import Hero from '../UI/Sections/Hero';
import Milestones from '../UI/Sections/Milestones';
import Navbar from '../UI/Sections/Navbar';
import NewsPage from '../UI/Sections/NewsPage';
import PrivacyPolicyPage from '../UI/Sections/PrivacyPolicyPage';
import ServicesPage from '../UI/Sections/ServicesPage';
import TermsPage from '../UI/Sections/TermsPage';
import VideoPage from '../UI/Sections/VideoPage';

// ─── URL ↔ Page Mappings ──────────────────────────────────────────────────────
// Maps URL slug  →  internal page key
const SLUG_TO_PAGE = {
    'about':          'about',
    'milestones':     'milestones',
    'services':       'services',
    'account-open':   'account-open',
    'news':           'news',
    'downloads':      'downloads',
    'privacy-policy': 'privacy-policy',
    'terms':          'terms-page',
    'media':          'video-page',
};

// Maps internal page key  →  URL slug (empty = home / base path)
const PAGE_TO_SLUG = {
    'home':           '',
    'about':          'about',
    'milestones':     'milestones',
    'services':       'services',
    'account-open':   'account-open',
    'news':           'news',
    'downloads':      'downloads',
    'privacy-policy': 'privacy-policy',
    'terms-page':     'terms',
    'video-page':     'media',
    'blog-post':      '',   // handled via ?post_id query param
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Return the WordPress base path for this site (set by PHP via window.gmfHomePath).
 * Falls back to '/' so the code works in any environment.
 */
function getBasePath() {
    return (typeof window !== 'undefined' && window.gmfHomePath)
        ? window.gmfHomePath
        : '/';
}

/**
 * Build the browser URL path for a given internal page.
 */
function buildPath(page, postId = null) {
    const base = getBasePath(); // e.g. '/'  or '/subdir/'
    const slug = PAGE_TO_SLUG[page];

    if (slug) {
        // e.g. '/privacy-policy'  or  '/subdir/privacy-policy'
        return base.replace(/\/$/, '') + '/' + slug;
    }

    if (page === 'blog-post' && postId) {
        // Keep blog posts on the base path with a query param
        const url = new URL(window.location.href);
        url.pathname = base.replace(/\/$/, '') || '/';
        url.search = '';
        url.searchParams.set('post_id', postId);
        return url.pathname + url.search;
    }

    // Home
    return base;
}

/**
 * Inspect window.location and return { page, postId } for the current URL.
 * Returns null when the URL doesn't match any known GMF route (don't force a change).
 */
function detectPageFromLocation() {
    const { pathname, search } = window.location;

    // Existing ?post_id behaviour
    const urlParams = new URLSearchParams(search);
    const postId = urlParams.get('post_id');
    if (postId) {
        return { page: 'blog-post', postId: parseInt(postId, 10) };
    }

    const base = getBasePath(); // e.g. '/'

    // Strip the base prefix, then get the last non-empty segment
    let relPath = pathname;
    if (base !== '/' && relPath.startsWith(base.replace(/\/$/, ''))) {
        relPath = relPath.slice(base.replace(/\/$/, '').length) || '/';
    }
    const slug = relPath.replace(/^\/+|\/+$/g, ''); // trim slashes

    if (!slug) {
        return { page: 'home', postId: null };
    }

    const page = SLUG_TO_PAGE[slug];
    if (page) {
        return { page, postId: null };
    }

    return null; // Unknown path – keep the default page
}

// ─── Component ────────────────────────────────────────────────────────────────

const SiteLayoutContainer = ({ attributes, isEditor = false, setClientTheme }) => {
    const { activePage, primaryColor = '#ff0037', hero = {} } = attributes;
    const [currentPage, setCurrentPage] = useState(activePage || 'home');
    const [currentPostId, setCurrentPostId] = useState(null);

    // On first mount (frontend only) — derive page from the current URL
    useEffect(() => {
        if (isEditor) return;
        const detected = detectPageFromLocation();
        if (detected) {
            setCurrentPage(detected.page);
            setCurrentPostId(detected.postId);
        }
    }, [isEditor]);

    const page = isEditor ? activePage : currentPage;

    // Navigate to a named page and push the clean URL path
    const handleNavigate = (newPage) => {
        setCurrentPage(newPage);
        setCurrentPostId(null);
        if (!isEditor) {
            const path = buildPath(newPage);
            window.history.pushState({ gmfPage: newPage, postId: null }, '', path);
        }
    };

    // Navigate to a blog post (path stays on base, post_id added as query param)
    const handleNavigateToPost = (postId) => {
        setCurrentPostId(postId);
        setCurrentPage('blog-post');
        if (!isEditor) {
            const path = buildPath('blog-post', postId);
            window.history.pushState({ gmfPage: 'blog-post', postId }, '', path);
        }
    };

    // Handle browser back / forward buttons
    useEffect(() => {
        if (isEditor) return;

        const handlePopState = (event) => {
            const state = event.state;
            if (state && state.gmfPage) {
                // State was set by pushState above
                setCurrentPage(state.gmfPage);
                setCurrentPostId(state.postId || null);
            } else {
                // Initial history entry (no state) — re-detect from URL
                const detected = detectPageFromLocation();
                if (detected) {
                    setCurrentPage(detected.page);
                    setCurrentPostId(detected.postId);
                } else {
                    setCurrentPage('home');
                    setCurrentPostId(null);
                }
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [isEditor]);

    const renderPage = () => {
        switch (page) {
            case 'about':        return <AboutUs attributes={attributes} />;
            case 'milestones':   return <Milestones attributes={attributes} />;
            case 'downloads':    return <Downloads attributes={attributes} />;
            case 'services':     return <ServicesPage attributes={attributes} />;
            case 'account-open': return <AccountOpenPage attributes={attributes} />;
            case 'news':         return <NewsPage attributes={attributes} />;
            case 'blog-post':    return (
                <BlogPostDetail
                    postId={currentPostId}
                    onBack={() => handleNavigate('home')}
                    primaryColor={primaryColor}
                    blogSettings={hero.blogSettings || {}}
                />
            );
            case 'video-page':    return <VideoPage attributes={attributes} onBack={() => handleNavigate('home')} />;
            case 'privacy-policy': return <PrivacyPolicyPage attributes={attributes} onBack={() => handleNavigate('home')} />;
            case 'terms-page':    return <TermsPage attributes={attributes} onBack={() => handleNavigate('home')} />;
            default:              return <Hero attributes={attributes} onNavigate={handleNavigateToPost} />;
        }
    };

    return (
        <div className="gmfSiteLayout">
            <Navbar attributes={attributes} currentPage={page} onNavigate={handleNavigate} setClientTheme={setClientTheme} />
            <main className="gmfMain">
                {renderPage()}
            </main>
            <Footer attributes={attributes} onNavigate={handleNavigate} />
        </div>
    );
};

export default SiteLayoutContainer;
